import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Blog.css';

const blogs = [
  {
    id: 1,
    category: 'BPO Insights',
    title: 'How Indian BPO Firms Are Capturing the Global Outsourcing Market in 2024',
    excerpt: 'The global outsourcing landscape is shifting rapidly. Indian BPO providers, armed with cost efficiency, English fluency, and growing technology capabilities, are commanding larger contracts from US and UK enterprises than ever before.',
    date: 'December 12, 2024',
    author: 'CSR Editorial Team',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1556761175-4b46a572b786?w=900&q=80',
    featured: true,
    content: `
      <h2>The BPO Revolution Is Indian-Led</h2>
      <p>India has long been synonymous with business process outsourcing, but 2024 marks a turning point. No longer just a cost-arbitrage play, Indian BPO firms are now recognised as strategic partners delivering genuine value-add — from AI-enhanced customer service to real-time analytics and compliance management.</p>
      <p>Global enterprises, particularly from the United States and United Kingdom, are signing longer-term contracts and expanding the scope of services they delegate to Indian partners. The drivers are multifold: a maturing talent pool, robust digital infrastructure, and a post-pandemic normalisation of remote and distributed operations.</p>
      <blockquote><p>"Cost reduction was once the entire conversation. Now clients ask about process innovation, data security, and business continuity. Indian BPO has answered all three." — Industry Analyst, Gartner</p></blockquote>
      <h2>What's Changing in Client Expectations</h2>
      <p>Clients today arrive with a far more sophisticated understanding of outsourcing. They want SLAs with teeth, real-time dashboards, and agents who understand their brand voice. This has forced Indian BPO providers — including Bangalore-based specialists like CSR's Financial Solutions — to invest heavily in training, technology, and quality frameworks.</p>
      <ul>
        <li>24/7 multilingual support capabilities across voice, email, and chat</li>
        <li>AI-powered call analytics and sentiment monitoring</li>
        <li>ISO-certified data security and GDPR-compliant operations</li>
        <li>Scalable team structures that flex with seasonal demand</li>
      </ul>
      <h2>Bangalore: The BPO Capital of South India</h2>
      <p>Bangalore's ecosystem offers a unique combination of engineering talent, English language proficiency, and startup culture that makes it uniquely positioned to serve the most demanding global clients. For financial services BPO specifically, the city's proximity to major banking and fintech clusters creates a talent pipeline unlike any other Indian city.</p>
      <p>At CSR's Financial Solutions, we've seen direct evidence of this shift: our international client roster has grown substantially as US and UK businesses discover the operational excellence available in Bangalore at a fraction of their domestic operating costs.</p>
      <h2>The Road Ahead</h2>
      <p>The next wave of BPO growth will be driven by hyper-automation, where AI handles tier-1 queries and human agents are reserved for complex resolution and relationship management. Indian BPO firms that invest in this hybrid model now will define the industry for the next decade.</p>
    `
  },
  {
    id: 2,
    category: 'Debt Recovery',
    title: 'Ethical Debt Recovery: Why Compliance-First Collections Deliver Better Results',
    excerpt: 'The old playbook of aggressive debt collection is dead. Compliance-first approaches aligned with RBI guidelines not only protect brands — they consistently deliver higher recovery rates by preserving customer relationships.',
    date: 'November 28, 2024',
    author: 'CSR Editorial Team',
    readTime: '5 min read',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=900&q=80',
    featured: false,
    content: `
      <h2>The Case for Ethical Collections</h2>
      <p>Across the collections industry, a fundamental shift is underway. Banks and NBFCs that once prioritised aggressive recovery tactics are discovering that compliance-first, dignity-preserving approaches not only protect their brand reputation — they produce measurably better recovery outcomes.</p>
      <p>The Reserve Bank of India's Fair Practices Code has codified many of these principles, but leading collection agencies have gone further, embedding ethical practices into every touchpoint of the recovery process.</p>
      <h2>Why Aggressive Tactics Backfire</h2>
      <ul>
        <li>Customers who feel harassed are less likely to settle willingly — legal disputes multiply</li>
        <li>Social media amplification means one bad recovery incident can damage a bank's brand significantly</li>
        <li>Regulatory penalties under RBI and TRAI guidelines have become more stringent</li>
        <li>High turnover among collectors trained in coercive techniques adds operational cost</li>
      </ul>
      <blockquote><p>"The best recovery conversations are ones where the customer leaves feeling heard. Settlement rates are dramatically higher when dignity is preserved throughout." — Chandrahas S, Founder, CSR's Financial Solutions</p></blockquote>
      <h2>The CSR Approach: Negotiation Over Coercion</h2>
      <p>At CSR's Financial Solutions, our field recovery teams are trained in communication psychology, financial hardship assessment, and creative settlement structuring. The result: a 95%+ recovery rate that is built on sustainable practices rather than short-term pressure tactics.</p>
      <p>Our Karnataka-wide field network combines local language fluency with cultural sensitivity, ensuring that every recovery engagement respects the customer's situation while effectively representing our clients' interests.</p>
      <h2>The Regulatory Horizon</h2>
      <p>RBI is expected to strengthen its oversight of collections practices further over the coming years. Collection agencies that build compliance-first DNA now will be well-positioned as these regulations tighten, while those relying on outdated aggressive techniques will face increasing legal and operational risk.</p>
    `
  },
  {
    id: 3,
    category: 'KPO & Analytics',
    title: 'Knowledge Process Outsourcing: The High-Value Tier of the Outsourcing Pyramid',
    excerpt: 'As AI automates routine BPO tasks, sophisticated Knowledge Process Outsourcing — driven by domain experts, not just data entry clerks — is becoming the highest-growth segment in India\'s outsourcing industry.',
    date: 'November 10, 2024',
    author: 'CSR Editorial Team',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=900&q=80',
    featured: false,
    content: `
      <h2>Beyond Data Entry: The KPO Opportunity</h2>
      <p>Knowledge Process Outsourcing represents the premium tier of the Indian outsourcing value chain. Where BPO handles volume-driven, process-centric tasks, KPO delivers insight, analysis, and domain expertise that directly informs strategic business decisions.</p>
      <p>For financial services clients specifically, KPO encompasses financial analysis, regulatory reporting, risk modelling, customer segmentation, and compliance advisory — functions that require not just competent execution but genuine domain knowledge.</p>
      <h2>The AI Complement Effect</h2>
      <p>Contrary to fears that AI would hollow out the KPO industry, the opposite is occurring. AI tools are amplifying the output of KPO analysts by automating data gathering and preliminary processing, freeing expert teams to focus on interpretation, recommendation, and client advisory. This is increasing both the quality and speed of KPO deliverables.</p>
      <ul>
        <li>Financial reconciliation that once took three days now takes hours</li>
        <li>Customer behavioural segmentation is enriched by ML pattern recognition</li>
        <li>Regulatory compliance monitoring is automated, with human review for edge cases</li>
        <li>Market research integrates real-time data feeds with analyst interpretation</li>
      </ul>
      <blockquote><p>"KPO clients don't want cheaper data entry. They want smarter decisions faster. That's the service we're building." — CSR KPO Division</p></blockquote>
      <h2>Who Benefits Most from KPO</h2>
      <p>NBFCs and fintech companies are emerging as the most enthusiastic adopters of KPO services, driven by aggressive growth targets and lean internal teams. Rather than building expensive in-house analytics departments, they outsource the entire intelligence function to specialised KPO providers.</p>
      <p>At CSR's Financial Solutions, our KPO practice serves clients across financial analytics, compliance reporting, and customer insight generation — with engagement models that flex from project-based to fully embedded team structures.</p>
      <h2>Building a KPO Partnership</h2>
      <p>The most successful KPO engagements are built on deep client integration: regular business reviews, aligned incentive structures, and knowledge transfer protocols that ensure continuity. The providers that treat KPO as a transactional commodity will struggle; those that build genuine strategic partnerships will grow.</p>
    `
  },
  {
    id: 4,
    category: 'HR Outsourcing',
    title: 'HR Outsourcing for Growing Businesses: Cut Costs, Retain Talent, Stay Compliant',
    excerpt: 'India\'s statutory compliance landscape for employers is complex and constantly evolving. HR outsourcing partners that combine payroll accuracy, PF/ESI expertise, and talent acquisition give growing businesses a decisive operational edge.',
    date: 'October 22, 2024',
    author: 'CSR Editorial Team',
    readTime: '5 min read',
    image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=900&q=80',
    featured: false,
    content: `
      <h2>The HR Compliance Burden on Indian Businesses</h2>
      <p>For any business with employees in India, statutory compliance is a perpetual challenge. PF contributions, ESI filings, professional tax, gratuity calculations, and the maze of state-specific labour laws create a compliance overhead that consumes disproportionate management attention — and carries serious penalty risk when mistakes occur.</p>
      <p>HR outsourcing partners who genuinely understand this landscape — not just the national framework but the Karnataka-specific nuances — deliver immediate value by eliminating compliance risk and freeing leadership to focus on growth.</p>
      <h2>Beyond Payroll: The Full HR Lifecycle</h2>
      <ul>
        <li>Talent acquisition and structured interviewing processes that improve candidate quality</li>
        <li>Onboarding programmes that accelerate new hire productivity</li>
        <li>Performance management frameworks aligned with business objectives</li>
        <li>Training and development programmes that reduce attrition</li>
        <li>Exit processing and full and final settlement management</li>
      </ul>
      <blockquote><p>"Growing businesses rarely have the luxury of a fully-staffed HR department. A qualified outsourcing partner can deliver enterprise-grade HR operations at a fraction of the cost." — Industry Report, NASSCOM</p></blockquote>
      <h2>The Talent Retention Equation</h2>
      <p>In Bangalore's competitive talent market, retention is often more valuable than acquisition. HR outsourcing partners who implement structured recognition programmes, clear career pathways, and responsive employee relations processes can meaningfully reduce the attrition that costs businesses two to three months' salary per departed employee.</p>
      <p>At CSR's Financial Solutions, our HRO practice is particularly well-suited to businesses in the BPO, financial services, and technology sectors — industries where we have deep familiarity with role profiles, compensation benchmarks, and retention dynamics.</p>
      <h2>Getting Started with HR Outsourcing</h2>
      <p>The transition to HR outsourcing is simpler than most business leaders expect. A well-structured handover covering existing payroll data, employee records, and current compliance status can be completed within two to three weeks. The payoff — in management bandwidth, compliance confidence, and employee experience — is typically evident within the first quarter.</p>
    `
  }
];

function PostCard({ post }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="post-card">
      <div className="post-card-img">
        <img src={post.image} alt={post.title} loading="lazy" />
      </div>
      <div className="post-card-body">
        <span className="post-category">{post.category}</span>
        <h3 className="post-title">{post.title}</h3>
        <p className="post-excerpt">{post.excerpt}</p>
        <div className="post-meta">
          <span><i className="fas fa-calendar"></i>{post.date}</span>
          <span><i className="fas fa-clock"></i>{post.readTime}</span>
        </div>
        <button
          className="read-more"
          onClick={() => setExpanded(!expanded)}
          style={{background:'none',border:'none',cursor:'pointer',padding:0,fontFamily:'inherit'}}
        >
          {expanded ? 'Close' : 'Read Article'} <i className={`fas fa-arrow-${expanded?'up':'right'}`}></i>
        </button>
        {expanded && (
          <div className="post-body" style={{marginTop:'24px',paddingTop:'24px',borderTop:'1px solid var(--border)'}}
            dangerouslySetInnerHTML={{__html: post.content}}
          />
        )}
      </div>
    </div>
  );
}

export default function Blog() {
  const [featuredExpanded, setFeaturedExpanded] = useState(false);
  const featured = blogs[0];
  const rest = blogs.slice(1);

  return (
    <div className="blog-page">
      <section className="blog-hero">
        <div className="bh-grid"></div>
        <div className="bh-photo" style={{backgroundImage:"url('https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=1400&q=80')"}}></div>
        <div className="container bh-inner">
          <div className="page-tag">Insights &amp; Resources</div>
          <h1 className="page-h">The CSR <span style={{color:'var(--gold-lt)',fontStyle:'italic'}}>Knowledge Hub</span></h1>
          <p className="page-sub">Industry insights, thought leadership and practical guides on BPO, KPO, debt recovery, and HR outsourcing in India and beyond.</p>
        </div>
      </section>

      <section className="blog-grid-section">
        <div className="container">
          {/* Featured Post */}
          <div className="featured-post">
            <div className="featured-post-img">
              <img src={featured.image} alt={featured.title} />
            </div>
            <div className="featured-post-body">
              <div>
                <span className="post-category featured-badge">Featured</span>
                <span className="post-category">{featured.category}</span>
              </div>
              <h2 className="post-title">{featured.title}</h2>
              <p className="post-excerpt">{featured.excerpt}</p>
              <div className="post-meta">
                <span><i className="fas fa-calendar"></i>{featured.date}</span>
                <span><i className="fas fa-clock"></i>{featured.readTime}</span>
              </div>
              <button
                className="read-more"
                onClick={() => setFeaturedExpanded(!featuredExpanded)}
                style={{background:'none',border:'none',cursor:'pointer',padding:0,fontFamily:'inherit'}}
              >
                {featuredExpanded ? 'Close Article' : 'Read Full Article'} <i className={`fas fa-arrow-${featuredExpanded?'up':'right'}`}></i>
              </button>
              {featuredExpanded && (
                <div className="post-body" style={{marginTop:'24px',paddingTop:'24px',borderTop:'1px solid var(--border)'}}
                  dangerouslySetInnerHTML={{__html: featured.content}}
                />
              )}
            </div>
          </div>

          {/* Post Grid */}
          <div style={{marginBottom:'48px'}}>
            <div className="eyebrow">All Articles</div>
            <h2 className="section-h">Latest <span className="gold">Insights</span></h2>
          </div>
          <div className="posts-grid">
            {rest.map(post => <PostCard key={post.id} post={post} />)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{padding:'100px 0',background:'var(--parchment)',borderTop:'1px solid var(--border)'}}>
        <div className="container" style={{textAlign:'center'}}>
          <div className="eyebrow" style={{justifyContent:'center'}}>Stay Informed</div>
          <h2 className="section-h">Want Expert Outsourcing <span className="gold">Advice?</span></h2>
          <p style={{fontSize:'0.9rem',color:'var(--text-lt)',marginTop:'16px',maxWidth:'480px',margin:'16px auto 40px',lineHeight:'1.8'}}>Talk to our team about how CSR's outsourcing expertise can transform your operations.</p>
          <Link to="/contact" className="btn-navy"><i className="fas fa-comments"></i> Speak With Us</Link>
        </div>
      </section>
    </div>
  );
}
